/**
 * Google Ads conversion upload history for one booking.
 *
 * Answers the question the browser-based tag could never answer: did this paid
 * booking actually reach Google Ads, and if not, why not? Every attempt — and
 * every refusal — is recorded in `google_ads_conversions`, so this reads a
 * ledger rather than guessing from logs.
 *
 * Round trips produce ONE row for the payment, recorded against the outbound
 * leg, so opening the return leg shows the same conversion.
 *
 * ADMIN-ONLY: hidden from non-admin users (mirrors GHLSyncLogsSection and
 * ZapierSyncLogsSection). The check happens inside the component so every mount
 * point inherits the policy. Note this is a UI gate — RLS on
 * google_ads_conversions still permits an account user to read their own rows
 * via the API, so tighten that too if the data itself must be admin-only.
 */

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Activity,
  Check,
  ChevronDown,
  ChevronRight,
  Copy,
  Loader2,
  RefreshCw,
  Send,
} from 'lucide-react'

import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { useToast } from '@/hooks/use-toast'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import {
  canRetryConversion,
  describeIdentifiers,
  presentConversionStatus,
} from './googleAdsConversionStatus'

// Local row type: google_ads_conversions isn't in the generated @limo/database
// types until `npm run db:types` runs post-migration, so the shape lives here to
// keep builds independent of type-regeneration order (same approach as
// ZapierSyncLogsSection).
interface GoogleAdsConversionRow {
  id: string
  account_id: string
  booking_id: string
  transaction_id: string
  event_type: string
  status: string
  error: string | null
  conversion_value: number | null
  currency: string | null
  gclid: string | null
  had_email: boolean
  had_phone: boolean
  validate_only: boolean
  attempt_count: number
  max_attempts: number
  next_retry_at: string | null
  last_attempt_at: string | null
  uploaded_at: string | null
  request_id: string | null
  response_status: number | null
  response_body: string | null
  event_payload: unknown
  event_timestamp: string | null
  conversion_action_id: string | null
  google_ads_customer_id: string | null
  created_at: string
  updated_at: string
}

interface GoogleAdsConversionSectionProps {
  bookingId: string
}

function formatDateTime(value: string | null): string {
  if (!value) return '—'
  return new Date(value).toLocaleString()
}

function formatMoney(value: number | null, currency: string | null): string {
  if (value === null || value === undefined) return '—'
  return `${currency ?? 'USD'} ${Number(value).toFixed(2)}`
}

/** Pretty-print the frozen request. Falls back to the raw value if it isn't JSON. */
function formatPayload(payload: unknown): string {
  try {
    return JSON.stringify(payload, null, 2)
  } catch {
    return String(payload)
  }
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5 sm:flex-row sm:items-baseline sm:gap-3">
      <span className="w-44 shrink-0 text-sm text-muted-foreground">{label}</span>
      <span className="break-all text-sm">{value}</span>
    </div>
  )
}

export function GoogleAdsConversionSection({ bookingId }: GoogleAdsConversionSectionProps) {
  const { isAdmin } = useAuth()
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [expanded, setExpanded] = useState(false)
  const [payloadCopied, setPayloadCopied] = useState(false)

  const queryKey = ['google-ads-conversion', bookingId]

  const { data: row, isLoading } = useQuery({
    queryKey,
    queryFn: async () => {
      const { data, error } = await (
        supabase.from('google_ads_conversions') as unknown as {
          select: (cols: string) => {
            eq: (
              col: string,
              val: string
            ) => {
              order: (
                col: string,
                opts: { ascending: boolean }
              ) => {
                limit: (n: number) => Promise<{
                  data: GoogleAdsConversionRow[] | null
                  error: { message: string } | null
                }>
              }
            }
          }
        }
      )
        .select('*')
        .eq('booking_id', bookingId)
        .order('created_at', { ascending: false })
        .limit(1)

      if (error) throw new Error(error.message)
      return data?.[0] ?? null
    },
    // Skip the query entirely for non-admins so we don't waste a request.
    enabled: isAdmin && Boolean(bookingId),
  })

  const copyPayload = async () => {
    if (!row?.event_payload) return
    try {
      await navigator.clipboard.writeText(formatPayload(row.event_payload))
      setPayloadCopied(true)
      setTimeout(() => setPayloadCopied(false), 2000)
    } catch {
      toast({
        title: 'Could not copy',
        description: 'Your browser blocked clipboard access.',
        variant: 'destructive',
      })
    }
  }

  const upload = useMutation({
    mutationFn: async () => {
      // Invoke the edge function directly rather than just re-queueing, so the
      // operator gets the outcome now instead of waiting up to 5 minutes for
      // the cron drain. The function re-checks the per-account gate and the
      // caller's access to this booking.
      const { data, error } = await supabase.functions.invoke('send-to-google-ads', {
        body: { booking_id: bookingId, manual_retry: true },
      })

      if (error) throw new Error(error.message)
      return data as { status?: string; error?: string; reason?: string; deduped?: boolean }
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey })

      const status = result?.status ?? 'unknown'
      if (status === 'uploaded') {
        toast({
          title: 'Conversion uploaded',
          description: 'Google Ads accepted the conversion. It can take hours to appear in their UI.',
        })
      } else if (status === 'validated') {
        toast({
          title: 'Validated (dry run)',
          description:
            'Google accepted it but recorded nothing — validate-only mode is on.',
        })
      } else if (result?.deduped) {
        toast({
          title: 'Already sent',
          description: 'This conversion has already reached Google Ads.',
        })
      } else {
        toast({
          title: 'Not uploaded',
          description: result?.reason ?? result?.error ?? `Status: ${status}`,
          variant: 'destructive',
        })
      }
    },
    onError: (error: Error) => {
      toast({ title: 'Upload failed', description: error.message, variant: 'destructive' })
    },
  })

  if (!isAdmin) return null

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-64" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-16 w-full" />
        </CardContent>
      </Card>
    )
  }

  const presentation = presentConversionStatus(row?.status)
  const retryable = canRetryConversion(row?.status)

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-4 space-y-0">
        <CardTitle className="flex items-center gap-2">
          <Activity className="h-5 w-5" />
          Google Ads Conversion
        </CardTitle>

        <Button
          size="sm"
          variant="outline"
          onClick={() => upload.mutate()}
          disabled={upload.isPending || !retryable}
          title={
            retryable
              ? 'Send this booking to Google Ads now'
              : 'Already uploaded or currently sending'
          }
        >
          {upload.isPending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : row ? (
            <RefreshCw className="mr-2 h-4 w-4" />
          ) : (
            <Send className="mr-2 h-4 w-4" />
          )}
          {row ? 'Retry now' : 'Send now'}
        </Button>
      </CardHeader>

      <CardContent className="space-y-4">
        {!row ? (
          <p className="text-sm text-muted-foreground">
            No upload has been attempted for this booking. That&apos;s expected if the
            booking predates server-side conversions, or if it was never paid. Use
            <span className="font-medium"> Send now</span> to try it.
          </p>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant={presentation.variant}>{presentation.label}</Badge>
              {row.validate_only && <Badge variant="outline">validate-only</Badge>}
              <span className="text-sm text-muted-foreground">{presentation.meaning}</span>
            </div>

            {row.error && (
              <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3">
                <p className="text-sm font-medium text-destructive">Reason</p>
                <p className="break-all text-sm text-muted-foreground">{row.error}</p>
              </div>
            )}

            <div className="space-y-1.5">
              <DetailRow
                label="Value sent"
                value={formatMoney(row.conversion_value, row.currency)}
              />
              <DetailRow label="Matched on" value={describeIdentifiers(row)} />
              <DetailRow label="Last attempt" value={formatDateTime(row.last_attempt_at)} />
              {row.status === 'pending' && (
                <DetailRow label="Next retry" value={formatDateTime(row.next_retry_at)} />
              )}
              {row.uploaded_at && (
                <DetailRow label="Uploaded at" value={formatDateTime(row.uploaded_at)} />
              )}
              <DetailRow
                label="Attempts"
                value={`${row.attempt_count} of ${row.max_attempts}`}
              />
            </div>

            <Collapsible open={expanded} onOpenChange={setExpanded}>
              <CollapsibleTrigger asChild>
                <Button variant="ghost" size="sm" className="px-0">
                  {expanded ? (
                    <ChevronDown className="mr-1 h-4 w-4" />
                  ) : (
                    <ChevronRight className="mr-1 h-4 w-4" />
                  )}
                  Technical details
                </Button>
              </CollapsibleTrigger>

              <CollapsibleContent className="space-y-1.5 pt-2">
                <DetailRow label="Transaction ID" value={row.transaction_id} />
                <DetailRow
                  label="Conversion action"
                  value={row.conversion_action_id ?? '—'}
                />
                <DetailRow
                  label="Ads customer ID"
                  value={row.google_ads_customer_id ?? '—'}
                />
                <DetailRow
                  label="Event timestamp"
                  value={formatDateTime(row.event_timestamp)}
                />
                {/* Google's support correlator — the thing to quote if you ever
                    have to ask them why a conversion didn't land. */}
                <DetailRow label="Google request ID" value={row.request_id ?? '—'} />
                <DetailRow
                  label="Response status"
                  value={row.response_status ?? '—'}
                />
                {Boolean(row.event_payload) && (
                  <div className="space-y-1">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm text-muted-foreground">
                        Request sent to Google (events:ingest)
                      </span>
                      <Button variant="ghost" size="sm" onClick={copyPayload}>
                        {payloadCopied ? (
                          <Check className="mr-1 h-3.5 w-3.5" />
                        ) : (
                          <Copy className="mr-1 h-3.5 w-3.5" />
                        )}
                        {payloadCopied ? 'Copied' : 'Copy'}
                      </Button>
                    </div>
                    {/* The exact body frozen before the HTTP call, so this is what
                        Google actually received — not a reconstruction. Safe to
                        show: identifiers are SHA-256 hashes, never raw PII, and
                        the access token travelled in the header. */}
                    <pre className="max-h-80 overflow-auto rounded-md bg-muted p-3 text-xs">
                      {formatPayload(row.event_payload)}
                    </pre>
                  </div>
                )}

                {row.response_body && (
                  <div className="space-y-1">
                    <span className="text-sm text-muted-foreground">Response body</span>
                    <pre className="max-h-48 overflow-auto rounded-md bg-muted p-3 text-xs">
                      {row.response_body}
                    </pre>
                  </div>
                )}
              </CollapsibleContent>
            </Collapsible>
          </>
        )}
      </CardContent>
    </Card>
  )
}
